import { useRef } from 'react';
import constate from 'constate';
import { WS_INACCURACY } from 'constants/params';

const ERR_ID = 'audio-track.provider.ts';
const FRACTION_END = 0.9999;

type ClientX = number;

const useAudioTrackProvider = () => {
  const ws = useRef<WaveSurfer>();
  const wsRoot = useRef<HTMLDivElement>();
  const wsWave = useRef<HTMLDivElement | null>(null);
  const leftOffset = useRef(0);

  const isWsFinished = (time?: Seconds) => {
    if (!ws.current) {
      console.error(`[${ERR_ID}] isWsFinished`);
      return false;
    }
    return Math.abs(ws.current!.getCurrentTime() - (time ?? ws.current!.getDuration())) < WS_INACCURACY;
  };

  const wsGetCursorFraction = (clientX: ClientX): Fraction => {
    if (!wsWave.current) return 0;
    const xPos = clientX - leftOffset.current;
    const fraction = (xPos + wsWave.current.scrollLeft) / wsWave.current.scrollWidth;
    if (fraction < 0) return 0;
    if (fraction > 1) return 1;
    return fraction;
  };

  const wsGetCursorPosition = (fraction: Fraction): ClientX => {
    if (!wsWave.current) return 0;
    return fraction * wsWave.current.scrollWidth - wsWave.current.scrollLeft;
  };

  const wsGetDuration = () => {
    const duration = ws.current?.getDuration();
    if (!duration) {
      console.error(`[${ERR_ID}] wsGetDuration: duration is not available`);
      return 0;
    }
    return duration;
  };

  const wsGetCursorTime = (type: SeekToType | 'ms' = 'seconds') => {
    if (!ws.current) return 0;

    const currentTime = ws.current.getCurrentTime();
    if (!Number.isFinite(currentTime) || currentTime < 0) return 0;

    if (type === 'fraction') {
      const duration = wsGetDuration();
      if (!Number.isFinite(duration) || duration <= 0) return 0;
      return currentTime / duration;
    }
    if (type === 'ms') return currentTime * 1000;
    return currentTime;
  };

  const wsSeekTo = (amount: SeekToValue, type: SeekToType = 'seconds') => {
    if (!ws.current) {
      console.error(`[${ERR_ID}] wsSeekTo`);
      return;
    }
    if (type === 'seconds') ws.current.setCurrentTime(amount);
    else ws.current.seekTo(amount === 1 ? FRACTION_END : amount);
  };

  const wsPlay = (value?: boolean) => {
    if (!ws.current) {
      console.log(`[${ERR_ID}] wsPlay`);
      return;
    }
    const play = value ?? !ws.current.isPlaying();
    if (play) ws.current.play();
    else ws.current.pause();
  };

  return {
    ws,
    wsRoot,
    wsWave,
    leftOffset,
    wsGetDuration,
    wsGetCursorTime,
    wsGetCursorFraction,
    wsGetCursorPosition,
    isWsFinished,
    wsSeekTo,
    wsPlay,
  };
};

export const [AudioTrackProvider, useAudioTrackContext] = constate(useAudioTrackProvider);
